/**
 * Consent Redux Slice
 * GDPR-compliant consent state management
 */

import { createSlice, type PayloadAction } from '@ufc/core'
import type {
  ConsentState,
  ConsentRecord,
  UpdateConsentPayload,
  ConsentMethod,
} from '../domain/types'
import { CONSENT_STORAGE_KEY } from '../domain/types'

// ============================================
// Initial State
// ============================================

const initialState: ConsentState = {
  necessary: true, // Always true
  analytics: false,
  marketing: false,
  preferences: false,
  hasDecided: false,
  showBanner: true,
  settingsOpen: false,
}

// ============================================
// Helper Functions
// ============================================

/**
 * Load consent from localStorage
 */
function loadStoredConsent(): Partial<ConsentState> | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!stored) return null

    const record: ConsentRecord = JSON.parse(stored)
    return {
      necessary: true,
      analytics: record.state.analytics,
      marketing: record.state.marketing,
      preferences: record.state.preferences,
      hasDecided: record.state.hasDecided,
      showBanner: false,
    }
  } catch {
    return null
  }
}

/**
 * Save consent to localStorage
 */
function saveConsent(
  state: ConsentState,
  method: ConsentMethod,
  policyVersion: string
): void {
  if (typeof window === 'undefined') return

  const record: ConsentRecord = {
    state: {
      necessary: true,
      analytics: state.analytics,
      marketing: state.marketing,
      preferences: state.preferences,
      hasDecided: state.hasDecided,
    },
    metadata: {
      timestamp: new Date().toISOString(),
      policyVersion,
      method,
    },
  }

  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record))
}

// ============================================
// Slice
// ============================================

export const consentSlice = createSlice({
  name: 'consent',
  initialState,
  reducers: {
    // ==========================================
    // User Actions
    // ==========================================

    /**
     * Accept all cookie categories
     */
    acceptAll: (state, action: PayloadAction<{ policyVersion: string }>) => {
      state.necessary = true
      state.analytics = true
      state.marketing = true
      state.preferences = true
      state.hasDecided = true
      state.showBanner = false
      state.settingsOpen = false

      saveConsent(state, 'banner', action.payload.policyVersion)
    },

    /**
     * Reject non-essential cookies
     */
    rejectNonEssential: (state, action: PayloadAction<{ policyVersion: string }>) => {
      state.necessary = true
      state.analytics = false
      state.marketing = false
      state.preferences = false
      state.hasDecided = true
      state.showBanner = false
      state.settingsOpen = false

      saveConsent(state, 'banner', action.payload.policyVersion)
    },

    /**
     * Update a specific consent category
     */
    updateConsent: (
      state,
      action: PayloadAction<UpdateConsentPayload & { policyVersion: string }>
    ) => {
      const { category, granted, policyVersion } = action.payload

      if ((category as string) !== 'necessary') {
        state[category] = granted
        state.hasDecided = true

        saveConsent(state, 'settings', policyVersion)
      }
    },

    /**
     * Save current preferences (from settings modal)
     */
    savePreferences: (state, action: PayloadAction<{ policyVersion: string }>) => {
      state.hasDecided = true
      state.showBanner = false
      state.settingsOpen = false

      saveConsent(state, 'settings', action.payload.policyVersion)
    },

    // ==========================================
    // UI Actions
    // ==========================================

    /** Open consent settings modal */
    openSettings: (state) => {
      state.settingsOpen = true
    },

    /** Close consent settings modal */
    closeSettings: (state) => {
      state.settingsOpen = false
      // Keep banner visible if user hasn't decided
      if (!state.hasDecided) {
        state.showBanner = true
      }
    },

    /** Show consent banner */
    showBanner: (state) => {
      state.showBanner = true
    },

    /** Hide consent banner */
    hideBanner: (state) => {
      state.showBanner = false
    },

    // ==========================================
    // Initialization
    // ==========================================

    /**
     * Load stored consent from localStorage
     * Called on app initialization
     */
    loadStoredConsent: (state, action: PayloadAction<{ policyVersion: string }>) => {
      const stored = loadStoredConsent()

      if (stored) {
        // Check if policy version matches
        try {
          const record: ConsentRecord = JSON.parse(
            localStorage.getItem(CONSENT_STORAGE_KEY) || '{}'
          )

          if (record.metadata?.policyVersion === action.payload.policyVersion) {
            state.necessary = true
            state.analytics = stored.analytics ?? false
            state.marketing = stored.marketing ?? false
            state.preferences = stored.preferences ?? false
            state.hasDecided = stored.hasDecided ?? false
            state.showBanner = !stored.hasDecided
          } else {
            // Policy version changed, show banner again
            state.showBanner = true
            state.hasDecided = false
          }
        } catch {
          state.showBanner = true
        }
      }
    },

    /**
     * Reset consent (for testing or manual reset)
     */
    resetConsent: (state) => {
      state.necessary = true
      state.analytics = false
      state.marketing = false
      state.preferences = false
      state.hasDecided = false
      state.showBanner = true
      state.settingsOpen = false

      if (typeof window !== 'undefined') {
        localStorage.removeItem(CONSENT_STORAGE_KEY)
      }
    },
  },
})

// ============================================
// Exports
// ============================================

export const consentActions = consentSlice.actions
export const consentReducer = consentSlice.reducer
